import { inject, Injectable } from '@angular/core';
import { TokenAbstractService } from '../../abstract/token-abstract.service';
import { DecodedJWT, LegacyDecodedJWT, LoginResponse, LoginTokens } from '../../../../models';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthBackend } from '../../../../backend/auth.backend';

@Injectable({
  providedIn: 'root',
})
export class TokenHandler extends TokenAbstractService {
  protected override readonly tokenStorageKey = 'session_tokens';
  #authBackend = inject(AuthBackend);

  setToken({ tokens }: LoginResponse): void {
    if (tokens) {
      this.localStorageService.store(this.tokenStorageKey, tokens);
    }
  }

  getValidToken(): Observable<string | null> {
    const token = this.retrieveToken<LoginTokens>();

    return token && token.idToken && !this.isTokenExpired(token.idToken) ? of(token.idToken) : this.tryRefreshToken();
  }

  getWebsiteId(): number | null {
    const token = this.retrieveToken<LoginTokens>();
    return token ? +this.decodeJWT<DecodedJWT>(token.idToken)['custom:WebsiteId'] : null;
  }

  getAccessToken(): string {
    return this.retrieveToken<LoginTokens>().accessToken;
  }

  getEmail(): string | null {
    const token = this.retrieveToken<LoginTokens>();
    if (!token) {
      return null;
    }
    return this.decodeJWT<DecodedJWT>(token.idToken).email;
  }

  getRole(): string | string[] | null {
    const token = this.retrieveToken<LoginTokens>();
    return token ? this.decodeJWT<DecodedJWT>(token.idToken)['cognito:groups'] : null;
  }

  getUserId(): string | null {
    const token = this.retrieveToken<LoginTokens>();
    return token ? this.decodeJWT<DecodedJWT>(token.idToken)['custom:LegacyUserId'] : null;
  }

  tryRefreshToken(): Observable<string> {
    const token = this.retrieveToken<LoginTokens>();
    return !token || !token.refreshToken
      ? of(null)
      : this.#authBackend.refreshToken({ refreshToken: token.refreshToken, email: this.getEmail() }).pipe(
          map((response: LoginResponse) => {
            if (response) {
              this.setToken(response);
              return response.tokens.idToken;
            }
            return token.idToken;
          })
        );
  }
}
